/**
 * Tela do construtor de propostas.
 *
 * Formulário à esquerda, prévia ao vivo à direita. O preview usa `srcdoc` no `iframe`, com o **mesmo**
 * HTML que a Lambda vai gravar no S3 — é a decisão D3 em ação: o vendedor aprova exatamente o que o
 * cliente vai receber.
 *
 * O recálculo é debounced porque `precificar` e `renderizarProposta` rodam a cada tecla digitada.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  analisarCsvCalculadora,
  CONFIGURACAO_PADRAO,
  ErroCsv,
  faixaSafeStoragePorUsuarios,
  FOCO_ASSESSMENT_CURTO,
  formatarBrl,
  formatarPercentual,
  formatarUsd,
  NOME_COMERCIAL_PLANO,
  type PlanoSuporte,
  type ResponsavelCongelado,
  type Trilha,
} from '@emaster/dominio';

import { useAutenticacao } from '../autenticacao/contexto.tsx';
import { Aviso, Marcador, Numero, Secao, Selecao, Texto } from './Campos.tsx';
import {
  deploySugerido,
  gerarPrevia,
  precisaCotacaoDolar,
  temCsv,
  valoresIniciais,
  type ValoresFormulario,
} from './montarDocumento.ts';
import { descartarRascunho, recuperarRascunho, salvarRascunho } from './rascunho.ts';
import { publicarProposta, atualizarProposta, obterCotacao, meuPerfil } from '../api/cliente.ts';

const MS_DEBOUNCE = 400;

const PLANOS_POR_TRILHA: Readonly<Record<Trilha, readonly PlanoSuporte[]>> = {
  CS: ['nenhum', 'developer', 'business', 'enterprise'],
  PS: ['nenhum', 'enterprise'],
};

export interface PropsModoEdicao {
  /** Número da proposta sendo editada. Quando presente, o componente opera em modo edição. */
  readonly numero: string;
  /** Valores iniciais carregados do servidor. */
  readonly valoresIniciais: ValoresFormulario;
  /** URL pública da proposta, para exibir ao republicar. */
  readonly urlPublica: string;
}

function Resumo({ previa }: { readonly previa: ReturnType<typeof gerarPrevia> }) {
  const r = previa.resultados;
  if (r === null) return null;

  const linha = (rotulo: string, usd: number, brl: number | null, destaque = false) => (
    <div
      key={rotulo}
      className={`flex items-baseline justify-between gap-4 py-2 ${destaque ? 'border-t-2 border-brand-accent font-bold text-brand-blue' : 'border-t border-gray-100 text-gray-600'}`}
    >
      <span className="text-sm">{rotulo}</span>
      <span className="text-right text-sm whitespace-nowrap">
        {formatarUsd(usd)}
        {brl !== null && <span className="block text-xs text-gray-500">{formatarBrl(brl)}</span>}
      </span>
    </div>
  );

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
      <h2 className="text-lg font-bold text-brand-blue">Composição do preço</h2>
      <p className="mt-1 text-xs text-gray-500">
        Calculado pelo mesmo motor que o servidor usa na publicação.
      </p>

      <div className="mt-4">
        {r.custoLiquidoTotal !== null &&
          linha('Custo líquido AWS', r.custoLiquidoTotal.usd, r.custoLiquidoTotal.brl)}
        {linha(
          r.margemAplicada ? `Consumo com margem de ${r.margemPercentual}%` : 'Consumo informado',
          r.consumo.usd,
          r.consumo.brl,
        )}
        {r.custoSuporte.usd > 0 &&
          linha('Plano de suporte', r.custoSuporte.usd, r.custoSuporte.brl)}
        {r.licencasBackup.usd > 0 &&
          linha('eMaster Backup', r.licencasBackup.usd, r.licencasBackup.brl)}
        {r.plataformaSafeStorage.usd > 0 &&
          linha('eMaster SafeStorage', r.plataformaSafeStorage.usd, r.plataformaSafeStorage.brl)}
        {r.custosAdicionais.usd > 0 &&
          linha('Custos adicionais', r.custosAdicionais.usd, r.custosAdicionais.brl)}
        {r.reducaoMensal.usd > 0 && linha('Redução pelo crédito AWS', -r.reducaoMensal.usd, null)}
        {linha('Mensalidade estimada', r.mensalidadeEstimada.usd, r.mensalidadeEstimada.brl, true)}
      </div>

      {r.deployCustoTotal.usd > 0 && (
        <p className="mt-3 text-xs text-gray-500">
          Implantação: {formatarUsd(r.deployCustoTotal.usd)} — a faturar{' '}
          {formatarUsd(r.deployFaturado.usd)}
        </p>
      )}

      {r.exibirEconomia && (
        <p className="mt-2 text-sm font-semibold text-green-600">
          Economia no período: {formatarPercentual(r.economiaPercentual)}
        </p>
      )}
    </div>
  );
}

export function TelaConstrutor({ edicao }: { readonly edicao?: PropsModoEdicao }) {
  const { sessao } = useAutenticacao();
  const modoEdicao = edicao !== undefined;

  const [valores, setValores] = useState<ValoresFormulario>(() =>
    modoEdicao ? edicao.valoresIniciais : valoresIniciais('CS'),
  );
  const [motivoAlteracao, setMotivoAlteracao] = useState('');
  const [avisoCsv, setAvisoCsv] = useState<string | null>(null);
  const [erroCsv, setErroCsv] = useState<string | null>(null);
  /*
   * As três marcas de "alguém já decidiu isto" começam ligadas em modo edição.
   *
   * A regra que as governa é: a interface sugere enquanto ninguém decidiu, e para de sugerir a partir
   * do momento em que alguém decide. Numa proposta já publicada **tudo** já foi decidido — os valores
   * vieram do documento no servidor, foram para o cliente e valem como compromisso. Começar em
   * `false` fazia as sugestões atropelarem exatamente esses valores ao abrir a edição:
   *
   * - `deployCusto` negociado voltava para "um mês de consumo";
   * - a faixa do SafeStorage era rebaixada para a que a contagem de usuários sugere, desfazendo a
   *   escolha deliberada de contratar acima da faixa prevendo crescimento.
   *
   * Nenhum dos dois avisava nada. O vendedor reabria a proposta para trocar uma frase e republicava
   * com preço diferente do que o cliente tinha recebido.
   */
  const [deployEditado, setDeployEditado] = useState(modoEdicao);
  const [modalidadeDeployEditada, setModalidadeDeployEditada] = useState(modoEdicao);
  const [faixaSafeStorageEditada, setFaixaSafeStorageEditada] = useState(modoEdicao);
  const [rascunhoDe, setRascunhoDe] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [urlPublicada, setUrlPublicada] = useState<string | null>(
    modoEdicao ? edicao.urlPublica : null,
  );
  /** `true` quando o servidor trocou o endereço público e o link antigo deixou de valer. */
  const [linkRotacionado, setLinkRotacionado] = useState(false);
  const entradaCsv = useRef<HTMLInputElement>(null);

  /*
   * Trava síncrona contra duplo-clique.
   *
   * `publicando` é estado do React, e portanto assíncrono: entre o clique e a re-renderização que
   * desabilita o botão cabe um segundo clique. O resultado seriam duas propostas com números
   * diferentes para o mesmo cliente, e o vendedor mandaria o link errado sem saber.
   */
  const publicacaoEmCurso = useRef(false);

  /**
   * Perfil pessoal do vendedor, obtido do endpoint `/usuarios/eu`.
   * Usado para a assinatura da proposta e para decidir se pode usar trilha PS.
   */
  const [perfilPessoal, setPerfilPessoal] = useState<{
    sub: string;
    email: string | null;
    perfil: string | null;
    nome: string | null;
    sobrenome: string | null;
    cargo: string | null;
    telefone: string | null;
    whatsapp: string | null;
    fotoUrl: string | null;
    cadastroCompleto: boolean;
    trilhas: readonly string[];
  } | null>(null);

  useEffect(() => {
    if (sessao === null) return;
    void meuPerfil().then((r) => {
      if (r.ok) {
        setPerfilPessoal(r.dados);
      }
    });
  }, [sessao]);

  /**
   * Responsável derivado do perfil pessoal.
   *
   * Devolve `null` quando falta telefone, em vez de recorrer a um número genérico. O fallback
   * anterior era `+55 31 3055-5000`, que nem passava na regra de celular do schema -- e se passasse
   * seria pior, porque imprimiria na proposta do cliente um telefone que não atende o vendedor.
   *
   * A verificação usa `cadastroCompleto`, e não `??`: a API devolve `""` para campo não preenchido,
   * e `""` não é nulo, então `??` deixava passar. Foi exatamente essa a causa do bug.
   */
  const responsavel = useMemo<ResponsavelCongelado | null>(() => {
    if (perfilPessoal === null) return null;

    /* Sem cadastro completo, o documento sairia com telefone vazio ou inexistente. */
    if (!perfilPessoal.cadastroCompleto) return null;

    /* Sem e-mail corporativo resolvido, o schema recusaria — melhor bloquear aqui com mensagem clara. */
    if (perfilPessoal.email === null || perfilPessoal.email === '') return null;

    const nomeCompleto = [perfilPessoal.nome, perfilPessoal.sobrenome]
      .filter((p) => p !== null && p !== '')
      .join(' ');
    return {
      sub: perfilPessoal.sub,
      email: perfilPessoal.email,
      nomeExibicao: nomeCompleto !== '' ? nomeCompleto : perfilPessoal.email,
      cargoExibicao: perfilPessoal.cargo ?? 'eMaster Cloud & Security',
      telefone: perfilPessoal.telefone ?? '',
      whatsapp: perfilPessoal.whatsapp ?? '',
      fotoUrl: perfilPessoal.fotoUrl,
    };
  }, [perfilPessoal]);

  /*
   * Recupera o rascunho na abertura — **só na criação de proposta nova**.
   *
   * Em modo edição este efeito sobrescrevia `edicao.valoresIniciais`, que acabou de vir do servidor,
   * pelo que o vendedor tinha digitado numa sessão anterior. O sintoma era exatamente o relatado:
   * abrir a edição de uma proposta e encontrar os campos de outra.
   *
   * O rascunho não tem identidade — a chave é só o `sub` do usuário —, então não há como saber a que
   * proposta ele pertence. E não precisa ter: em edição existe registro versionado e auditável no
   * DynamoDB, que é a persistência que vale. Recuperação local é uma rede de segurança para o
   * preenchimento do zero, onde não há nada no servidor a que voltar.
   */
  useEffect(() => {
    if (modoEdicao) return;
    if (sessao === null) return;

    const salvo = recuperarRascunho(sessao.sub);
    if (salvo !== null) {
      setValores(salvo.valores);
      setRascunhoDe(salvo.salvoEm);
    }
  }, [sessao, modoEdicao]);

  const atualizar = useCallback(
    <C extends keyof ValoresFormulario>(campo: C, v: ValoresFormulario[C]) => {
      setValores((atual) => ({ ...atual, [campo]: v }));
    },
    [],
  );

  // Prévia com atraso, para não recalcular a cada tecla.
  const [previa, setPrevia] = useState(() => gerarPrevia(valoresIniciais('CS'), null));

  useEffect(() => {
    const t = setTimeout(() => {
      setPrevia(gerarPrevia(valores, responsavel));

      /*
       * Gravar o rascunho em modo edição era a outra metade do mesmo defeito, e a mais traiçoeira:
       * como a chave é só o `sub`, editar uma proposta sobrescrevia o rascunho da proposta nova. O
       * vendedor abandonava a edição, clicava em "Nova proposta" e encontrava os campos da proposta
       * que tinha acabado de editar. Em edição não se grava rascunho: o servidor já guarda o
       * documento.
       */
      if (!modoEdicao && sessao !== null) salvarRascunho(sessao.sub, valores);
    }, MS_DEBOUNCE);

    return () => {
      clearTimeout(t);
    };
  }, [valores, responsavel, sessao, modoEdicao]);

  // Mantém o deploy sugerido em um mês de consumo, até o vendedor editar.
  useEffect(() => {
    if (deployEditado) return;
    if (valores.modalidadeDeploy === 'nao_incluido') return;

    const consumo = previa.resultados?.consumo;
    if (consumo === undefined) return;

    const naMoedaDeEntrada = valores.trilha === 'PS' ? (consumo.brl ?? consumo.usd) : consumo.usd;
    const sugerido = deploySugerido(naMoedaDeEntrada);

    if (sugerido !== valores.deployCusto) {
      setValores((atual) => ({ ...atual, deployCusto: sugerido }));
    }
  }, [
    previa.resultados,
    valores.modalidadeDeploy,
    valores.trilha,
    valores.deployCusto,
    deployEditado,
  ]);

  /*
   * Mantém a faixa do SafeStorage coerente com a contagem de usuários, até o vendedor escolher à mão.
   *
   * Quem decide a faixa é o domínio (`faixaSafeStoragePorUsuarios`), e não uma tabela repetida aqui.
   * O motor recusa faixa menor que a contagem, então sem esta sugestão o vendedor descobriria o
   * degrau errado pelo erro na prévia, em vez de já receber a faixa certa preenchida.
   */
  useEffect(() => {
    if (!valores.incluiSafeStorage) return;
    if (faixaSafeStorageEditada) return;

    const sugerida = faixaSafeStoragePorUsuarios(valores.safestorageUsuarios).plano;
    if (sugerida !== valores.safestoragePlano) {
      setValores((atual) => ({ ...atual, safestoragePlano: sugerida }));
    }
  }, [
    valores.incluiSafeStorage,
    valores.safestorageUsuarios,
    valores.safestoragePlano,
    faixaSafeStorageEditada,
  ]);

  async function handlePublicar() {
    if (previa.documento === null) return;

    if (modoEdicao && motivoAlteracao.trim() === '') {
      alert('Informe o motivo da alteração para republicar uma proposta já publicada.');
      return;
    }

    if (publicacaoEmCurso.current) return;
    publicacaoEmCurso.current = true;

    setPublicando(true);
    setUrlPublicada(null);
    setLinkRotacionado(false);
    try {
      const documento = modoEdicao
        ? {
            ...previa.documento,
            numero: edicao.numero,
            auditoria: { ...previa.documento.auditoria, motivoAlteracao: motivoAlteracao.trim() },
          }
        : previa.documento;

      const r = modoEdicao
        ? await atualizarProposta(edicao.numero, documento)
        : await publicarProposta(documento);

      if (r.ok) {
        const dados = r.dados as {
          url?: string;
          numero?: string;
          versao?: number;
          linkRotacionado?: boolean;
          urlAnterior?: string | null;
        };
        setUrlPublicada(dados.url ?? null);
        setLinkRotacionado(dados.linkRotacionado === true);
        const acao = modoEdicao ? 'atualizada' : 'publicada';

        /*
         * Aviso da troca de endereço.
         *
         * O servidor rotaciona o caminho público das propostas que ainda estavam no endereço antigo
         * e adivinhável. É uma correção de segurança, mas tem um efeito visível para o vendedor: o
         * link que ele já mandou ao cliente para de abrir. Avisar aqui é o que separa "o link mudou,
         * reenvie" de um cliente reclamando de página não encontrada.
         */
        const avisoLink =
          dados.linkRotacionado === true
            ? '\n\nATENÇÃO: o endereço desta proposta mudou por segurança. O link enviado antes ao cliente não abre mais — reenvie o link novo acima.'
            : '';

        alert(
          `Proposta ${acao}!\n\nURL: ${dados.url ?? '(indisponível)'}\nNúmero: ${dados.numero ?? ''}\nVersão: ${String(dados.versao ?? '')}${avisoLink}`,
        );
        if (!modoEdicao && sessao !== null) descartarRascunho(sessao.sub);
      } else {
        const dados = r.dados as { mensagem?: string };
        alert(
          `Erro ao ${modoEdicao ? 'atualizar' : 'publicar'}: ${dados.mensagem ?? `HTTP ${r.status}`}`,
        );
      }
    } catch (e) {
      alert(
        `Falha na comunicação com o servidor: ${e instanceof Error ? e.message : 'erro desconhecido'}`,
      );
    } finally {
      setPublicando(false);
      publicacaoEmCurso.current = false;
    }
  }

  async function importarCsv(arquivo: File) {
    setErroCsv(null);
    setAvisoCsv(null);

    try {
      const texto = await arquivo.text();
      const r = analisarCsvCalculadora(texto);

      setValores((atual) => ({
        ...atual,
        itensCusto: r.itens,
        regiaoAws: r.regiao,
        consumoManual: null,
      }));
      setDeployEditado(false);

      const partes = [`${r.itens.length} itens importados.`, ...r.avisos];
      setAvisoCsv(partes.join(' '));
    } catch (e) {
      setErroCsv(e instanceof ErroCsv ? e.message : 'Não foi possível ler o arquivo.');
    }
  }

  function limparCsv() {
    setValores((atual) => ({ ...atual, itensCusto: null, regiaoAws: null }));
    setAvisoCsv(null);
    setErroCsv(null);
    if (entradaCsv.current !== null) entradaCsv.current.value = '';
  }

  const podeTrilhaPs = perfilPessoal?.trilhas.includes('PS') ?? false;
  const temDuasTrilhas =
    perfilPessoal !== null &&
    perfilPessoal.trilhas.includes('CS') &&
    perfilPessoal.trilhas.includes('PS');

  // Estado para controle da cotação do dia (PTAX)
  const [cotacaoCarregando, setCotacaoCarregando] = useState(false);
  const [cotacaoEhReserva, setCotacaoEhReserva] = useState(false);
  const [ptaxModo, setPtaxModo] = useState<'cotacao_dia' | 'manual'>(
    valores.ptaxOrigem === 'manual' ? 'manual' : 'cotacao_dia',
  );

  /*
   * A cotação é necessária sempre que algum valor da proposta nasce em reais.
   *
   * Em PS isso é toda a proposta. Em CS é o eMaster Backup, cujo preço de licença é tabelado em
   * reais e precisa ser convertido para compor a mensalidade em dólar. Quem responde é o domínio,
   * pelo mesmo critério que o motor de precificação usa — ver `precisaCotacaoDolar`.
   */
  const precisaPtax = precisaCotacaoDolar(valores);

  // Busca a cotação quando ela é necessária e o modo é "cotação do dia"
  const buscarCotacaoDia = useCallback(async () => {
    setCotacaoCarregando(true);
    setCotacaoEhReserva(false);
    try {
      const r = await obterCotacao();
      if (r.ok) {
        const dados = r.dados as { taxa: number; ehReserva?: boolean };
        setValores((atual) => ({ ...atual, ptax: dados.taxa, ptaxOrigem: 'api' }));
        setCotacaoEhReserva(dados.ehReserva === true);
      }
    } catch {
      // Em caso de falha na comunicação, mantém o valor anterior.
    } finally {
      setCotacaoCarregando(false);
    }
  }, []);

  /*
   * Assim que a cotação passa a ser necessária, busca automaticamente. Vale para a troca de trilha e
   * para o vendedor de CS que acabou de lançar licenças de backup.
   *
   * **Só busca quando ainda não há cotação.** Sem essa condição, abrir a edição de uma proposta em PS
   * disparava a busca e substituía o PTAX do documento pelo dólar de hoje — reprecificando em silêncio
   * toda a coluna em reais que o cliente já tinha recebido. O próprio campo declara a intenção
   * contrária: a cotação é congelada no documento para a proposta poder ser reconstruída depois.
   *
   * As duas transições que o efeito existe para atender continuam funcionando, porque nelas a cotação
   * é nula: em CS sem nada em reais o PTAX é zerado pelo efeito abaixo, então trocar para PS ou lançar
   * licenças de backup cai aqui com `ptax` em nulo. E escolher "cotação do dia" no rádio busca pelo
   * próprio handler, sem depender deste efeito.
   */
  useEffect(() => {
    if (!precisaPtax) return;
    if (ptaxModo !== 'cotacao_dia') return;
    if (valores.ptax !== null) return;

    void buscarCotacaoDia();
  }, [precisaPtax, ptaxModo, valores.ptax, buscarCotacaoDia]);

  /*
   * Devolve o PTAX a nulo quando ele deixa de ser necessário.
   *
   * Sem isso, uma cotação buscada para lançar backup em CS sobreviveria à remoção das licenças, e o
   * documento sairia com a pílula de câmbio e uma coluna em reais que a proposta não usa. O critério
   * passa a ser visível: a coluna BRL aparece quando existe algo cotado em reais, e só então.
   */
  useEffect(() => {
    if (!precisaPtax && valores.ptax !== null) {
      setValores((atual) => ({ ...atual, ptax: null, ptaxOrigem: 'nao_aplicavel' }));
    }
  }, [precisaPtax, valores.ptax]);

  // Força trilha CS quando o vendedor não tem permissão e a trilha está em PS
  useEffect(() => {
    if (!podeTrilhaPs && valores.trilha === 'PS') {
      const padrao = valoresIniciais('CS');
      setValores((atual) => ({
        ...atual,
        trilha: 'CS',
        planoSuporte: padrao.planoSuporte,
        ptaxOrigem: padrao.ptaxOrigem,
      }));
    }
  }, [podeTrilhaPs, valores.trilha]);

  const comCsv = temCsv(valores);
  const moedaEntrada = valores.trilha === 'PS' ? 'R$' : 'US$';

  return (
    <div className="grid gap-4 sm:gap-6 lg:grid-cols-[minmax(0,480px)_1fr]">
      <div className="space-y-6">
        {rascunhoDe !== null && (
          <Aviso tom="informacao">
            Rascunho recuperado de {new Date(rascunhoDe).toLocaleString('pt-BR')}.{' '}
            <button
              type="button"
              className="font-semibold underline"
              onClick={() => {
                if (sessao !== null) descartarRascunho(sessao.sub);
                setValores(valoresIniciais(valores.trilha));
                setRascunhoDe(null);
                setDeployEditado(false);
                setModalidadeDeployEditada(false);
                setFaixaSafeStorageEditada(false);
              }}
            >
              Começar do zero
            </button>
          </Aviso>
        )}

        {perfilPessoal !== null &&
          (!perfilPessoal.cadastroCompleto ||
            perfilPessoal.email === null ||
            perfilPessoal.email === '') && (
            <div
              role="alert"
              className="rounded-xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600 p-5 space-y-3"
            >
              <h3 className="text-base font-bold text-amber-800 dark:text-amber-200">
                Complete seu cadastro para publicar
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Os dados abaixo são impressos na proposta que o cliente recebe, e o botão de
                WhatsApp usa o seu número. Sem eles, a publicação fica bloqueada — mas você pode
                preencher o formulário e salvar como rascunho normalmente.
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm text-amber-700 dark:text-amber-300">
                {(perfilPessoal.email === null || perfilPessoal.email === '') && (
                  <li>
                    E-mail corporativo (não foi possível identificar — entre em contato com um
                    administrador)
                  </li>
                )}
                {(perfilPessoal.telefone === null || perfilPessoal.telefone === '') && (
                  <li>Telefone celular (aparece no botão de WhatsApp da proposta)</li>
                )}
                {(perfilPessoal.cargo === null || perfilPessoal.cargo === '') && (
                  <li>Cargo (aparece no card de contato da proposta)</li>
                )}
                {(perfilPessoal.nome === null || perfilPessoal.nome === '') && (
                  <li>Nome (aparece na assinatura da proposta)</li>
                )}
              </ul>
              <a
                href="/meu-perfil"
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.8}
                  stroke="currentColor"
                  className="h-4 w-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                  />
                </svg>
                Completar meu perfil
              </a>
            </div>
          )}

        <Secao titulo="Trilha e cliente">
          {temDuasTrilhas ? (
            <Selecao
              id="trilha"
              rotulo="Trilha comercial"
              valor={valores.trilha}
              opcoes={[
                { valor: 'CS', rotulo: 'CS — setor privado' },
                { valor: 'PS', rotulo: 'PS — setor público' },
              ]}
              onChange={(t) => {
                const padrao = valoresIniciais(t);
                setValores((atual) => ({
                  ...atual,
                  trilha: t,
                  planoSuporte: padrao.planoSuporte,
                  ptaxOrigem: padrao.ptaxOrigem,
                }));
                if (t === 'PS') {
                  setPtaxModo('cotacao_dia');
                }
              }}
              dica="A trilha define a moeda de entrada, a margem e os planos disponíveis."
            />
          ) : (
            <div>
              <p className="text-sm font-medium text-brand-blue">Trilha comercial</p>
              <p className="mt-1 text-sm text-gray-700">
                Trilha: {podeTrilhaPs ? 'Public Sector (PS)' : 'Commercial Sector (CS)'}
              </p>
            </div>
          )}
          <Texto
            id="cliente"
            rotulo="Cliente"
            obrigatorio
            valor={valores.cliente}
            onChange={(v) => {
              atualizar('cliente', v);
            }}
          />
          <Texto
            id="projeto"
            rotulo="Projeto ou solução"
            obrigatorio
            valor={valores.projeto}
            onChange={(v) => {
              atualizar('projeto', v);
            }}
          />
          <Texto
            id="validade"
            rotulo="Válida até"
            valor={valores.validaAte}
            onChange={(v) => {
              atualizar('validaAte', v);
            }}
            dica="Formato AAAA-MM-DD. Após esta data a proposta segue acessível, mas sinalizada como vencida."
          />
        </Secao>

        <Secao
          titulo="Texto de abertura (opcional)"
          descricao="Compõe o título e o parágrafo de entrada da proposta. Se você não preencher, valores padrão são gerados a partir do nome do cliente e do projeto."
        >
          <div>
            <Texto
              id="promessa"
              rotulo="Promessa de valor"
              valor={valores.promessaValor}
              onChange={(v) => {
                atualizar('promessaValor', v);
              }}
              placeholder="a infraestrutura de nuvem que acompanha o crescimento do negócio"
              dica={`O título fica: "Para a ${valores.cliente.trim() === '' ? '[cliente]' : valores.cliente}: ${valores.promessaValor.trim() === '' ? '[promessa]' : valores.promessaValor}"`}
            />
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-semibold text-brand-accent hover:underline">
                Sugestões de promessa de valor
              </summary>
              <ul className="mt-2 space-y-1 text-xs text-gray-600">
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'a infraestrutura de nuvem que acompanha o crescimento do negócio',
                    );
                  }}
                >
                  a infraestrutura de nuvem que acompanha o crescimento do negócio
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'o custo real de operar na nuvem, serviço a serviço, sem surpresa na fatura',
                    );
                  }}
                >
                  o custo real de operar na nuvem, serviço a serviço, sem surpresa na fatura
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'previsibilidade de custo e performance para sustentar a operação crítica',
                    );
                  }}
                >
                  previsibilidade de custo e performance para sustentar a operação crítica
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'a migração para AWS com risco controlado e sem interrupção do serviço',
                    );
                  }}
                >
                  a migração para AWS com risco controlado e sem interrupção do serviço
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'segurança, conformidade e governança desde o primeiro dia de operação',
                    );
                  }}
                >
                  segurança, conformidade e governança desde o primeiro dia de operação
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'a modernização do ambiente sem reescrever a aplicação',
                    );
                  }}
                >
                  a modernização do ambiente sem reescrever a aplicação
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'redução de custo com FinOps aplicado à sua realidade de consumo',
                    );
                  }}
                >
                  redução de custo com FinOps aplicado à sua realidade de consumo
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'alta disponibilidade multi-AZ com plano de recuperação testado',
                    );
                  }}
                >
                  alta disponibilidade multi-AZ com plano de recuperação testado
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'backup confiável e recuperação de desastre com RPO e RTO definidos',
                    );
                  }}
                >
                  backup confiável e recuperação de desastre com RPO e RTO definidos
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'a parceria técnica que sustenta a sua operação 24x7',
                    );
                  }}
                >
                  a parceria técnica que sustenta a sua operação 24x7
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'a base de nuvem que habilita analítica e inteligência artificial',
                    );
                  }}
                >
                  a base de nuvem que habilita analítica e inteligência artificial
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'conformidade com a LGPD aplicada à arquitetura, não só ao contrato',
                    );
                  }}
                >
                  conformidade com a LGPD aplicada à arquitetura, não só ao contrato
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'a saída do data center próprio com previsibilidade de custo e prazo',
                    );
                  }}
                >
                  a saída do data center próprio com previsibilidade de custo e prazo
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'promessaValor',
                      'escala elástica para sustentar picos de demanda sem provisionar excesso',
                    );
                  }}
                >
                  escala elástica para sustentar picos de demanda sem provisionar excesso
                </li>
              </ul>
            </details>
          </div>
          <div>
            <Texto
              id="subtitulo"
              rotulo="Subtítulo de abertura"
              multilinha
              valor={valores.subtituloHero}
              onChange={(v) => {
                atualizar('subtituloHero', v);
              }}
              placeholder="Proposta personalizada para impulsionar a sua jornada na nuvem AWS com a parceria eMaster."
            />
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-semibold text-brand-accent hover:underline">
                Sugestões de subtítulo
              </summary>
              <ul className="mt-2 space-y-1 text-xs text-gray-600">
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Proposta personalizada para impulsionar a sua jornada na nuvem AWS com a parceria eMaster.',
                    );
                  }}
                >
                  Proposta personalizada para impulsionar a sua jornada na nuvem AWS com a parceria
                  eMaster.
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Dimensionamento construído a partir do seu consumo real, sem estimativa de volumetria.',
                    );
                  }}
                >
                  Dimensionamento construído a partir do seu consumo real, sem estimativa de
                  volumetria.
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Cada linha desta proposta vem do que já foi medido no seu ambiente.',
                    );
                  }}
                >
                  Cada linha desta proposta vem do que já foi medido no seu ambiente.
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Arquitetura desenhada sobre as boas práticas do AWS Well-Architected Framework.',
                    );
                  }}
                >
                  Arquitetura desenhada sobre as boas práticas do AWS Well-Architected Framework.
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Projeção de investimento com margem de gestão transparente e memória de cálculo aberta.',
                    );
                  }}
                >
                  Projeção de investimento com margem de gestão transparente e memória de cálculo
                  aberta.
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Plano de migração em ondas, com janela de corte definida e rollback preparado.',
                    );
                  }}
                >
                  Plano de migração em ondas, com janela de corte definida e rollback preparado.
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Operação assistida pela eMaster, com acesso direto a arquitetos certificados AWS.',
                    );
                  }}
                >
                  Operação assistida pela eMaster, com acesso direto a arquitetos certificados AWS.
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Investimento previsível, com governança de custo desde a primeira fatura.',
                    );
                  }}
                >
                  Investimento previsível, com governança de custo desde a primeira fatura.
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Ambiente dimensionado para crescer com o negócio, sem refazer a arquitetura.',
                    );
                  }}
                >
                  Ambiente dimensionado para crescer com o negócio, sem refazer a arquitetura.
                </li>
                <li
                  className="cursor-pointer rounded px-2 py-1 hover:bg-gray-100"
                  onClick={() => {
                    atualizar(
                      'subtituloHero',
                      'Proposta elaborada a partir do levantamento técnico conduzido com o seu time.',
                    );
                  }}
                >
                  Proposta elaborada a partir do levantamento técnico conduzido com o seu time.
                </li>
              </ul>
            </details>
          </div>
        </Secao>

        {/*
         * Bloco próprio, e não um campo dentro de "Produtos e benefícios": são assuntos diferentes.
         * Aqui é texto editorial que o vendedor escreve; lá é catálogo de produtos que a eMaster
         * vende, e que vai crescer. Misturar os dois fazia o campo de texto livre parecer um atributo
         * do primeiro produto da lista.
         *
         * A posição na proposta gerada **não** muda: quem decide onde a seção de observações aparece
         * é o renderizador, não a ordem dos campos no formulário.
         */}
        <Secao
          titulo="Detalhes adicionais e condições comerciais (opcional)"
          descricao="Texto livre que vira uma seção de observações na proposta. Use para memória de cálculo, condições especiais ou premissas que o cliente precisa conhecer."
        >
          <Texto
            id="observacoes"
            rotulo="Observações e condições"
            multilinha
            valor={valores.observacoesHtml}
            onChange={(v) => {
              atualizar('observacoesHtml', v);
            }}
            placeholder="Insira aqui uma memória de cálculo, condições especiais, observações técnicas ou outras informações relevantes para o cliente..."
            dica="Aceita texto livre. Deixe em branco para a proposta sair sem a seção de observações."
          />
        </Secao>

        <Secao
          titulo="Infraestrutura"
          descricao="Importe o CSV da AWS Pricing Calculator para que a margem seja aplicada item a item."
        >
          <div>
            <label htmlFor="csv" className="block text-sm font-medium text-brand-blue">
              CSV da calculadora
            </label>
            <input
              id="csv"
              ref={entradaCsv}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const arquivo = e.target.files?.[0];
                if (arquivo !== undefined) void importarCsv(arquivo);
              }}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-3 file:rounded-md file:border-0 file:bg-brand-blue file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-brand-light"
            />
            <p className="mt-1 text-xs text-gray-500">
              A exportação precisa estar no idioma <strong>inglês</strong>.
            </p>
          </div>

          {erroCsv !== null && <Aviso tom="erro">{erroCsv}</Aviso>}
          {avisoCsv !== null && (
            <Aviso tom="informacao">
              {avisoCsv}{' '}
              <button type="button" className="font-semibold underline" onClick={limparCsv}>
                Remover CSV
              </button>
            </Aviso>
          )}

          <Numero
            id="consumo"
            rotulo={`Consumo mensal previsto (${moedaEntrada})`}
            prefixo={moedaEntrada}
            valor={comCsv ? (previa.resultados?.consumo.usd ?? null) : valores.consumoManual}
            desabilitado={comCsv}
            onChange={(v) => {
              atualizar('consumoManual', v);
            }}
            dica={
              comCsv
                ? 'Calculado automaticamente a partir do CSV com a margem aplicada.'
                : 'Sem CSV importado, o valor informado é tratado como preço de venda (sem margem adicional).'
            }
          />

          {precisaPtax && (
            <>
              {valores.trilha === 'CS' && (
                <Aviso tom="informacao">
                  As licenças do eMaster Backup têm preço de tabela em reais. A cotação abaixo
                  converte esse valor para compor a mensalidade em dólar, e fica declarada na
                  proposta junto da tabela de investimento.
                </Aviso>
              )}

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-brand-blue">Cotação do dólar</legend>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="ptax-modo"
                    checked={ptaxModo === 'cotacao_dia'}
                    onChange={() => {
                      setPtaxModo('cotacao_dia');
                      void buscarCotacaoDia();
                    }}
                  />
                  Cotação do dia
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="ptax-modo"
                    checked={ptaxModo === 'manual'}
                    onChange={() => {
                      setPtaxModo('manual');
                      setValores((atual) => ({ ...atual, ptaxOrigem: 'manual' }));
                      setCotacaoEhReserva(false);
                    }}
                  />
                  Informar manualmente
                </label>
              </fieldset>

              {cotacaoEhReserva && ptaxModo === 'cotacao_dia' && (
                <Aviso tom="atencao">
                  Não foi possível obter a cotação do dia. Usando R$ 6,00 como referência — confirme
                  ou informe manualmente.
                </Aviso>
              )}

              {cotacaoCarregando && <p className="text-xs text-gray-500">Buscando cotação...</p>}

              <Numero
                id="ptax"
                rotulo="Cotação do dólar"
                obrigatorio
                passo="0.0001"
                prefixo="R$"
                valor={valores.ptax}
                desabilitado={ptaxModo === 'cotacao_dia' && !cotacaoEhReserva}
                onChange={(v) => {
                  setValores((atual) => ({ ...atual, ptax: v, ptaxOrigem: 'manual' }));
                  setPtaxModo('manual');
                  setCotacaoEhReserva(false);
                }}
                dica="Congelada no documento, para a proposta poder ser reconstruída depois."
              />
            </>
          )}
        </Secao>

        <Secao
          titulo="Desenho de Arquitetura (opcional)"
          descricao="Inclua um diagrama da solução proposta para dar mais contexto técnico e valor visual à proposta."
        >
          <div>
            <label htmlFor="arquitetura" className="block text-sm font-medium text-brand-blue">
              Imagem do diagrama
            </label>
            <input
              id="arquitetura"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const arquivo = e.target.files?.[0];
                if (arquivo === undefined) return;
                const leitor = new FileReader();
                leitor.onload = () => {
                  if (typeof leitor.result === 'string') {
                    atualizar('arquiteturaDataUrl', leitor.result);
                  }
                };
                leitor.readAsDataURL(arquivo);
              }}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-3 file:rounded-md file:border-0 file:bg-brand-blue file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-brand-light"
            />
            <p className="mt-1 text-xs text-gray-500">
              PNG, JPEG ou WebP. Recomendado: exportação do draw.io em alta resolução.
            </p>
          </div>

          {valores.arquiteturaDataUrl !== '' && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold text-gray-500">Prévia do diagrama:</p>
              <img
                src={valores.arquiteturaDataUrl}
                alt="Diagrama de arquitetura"
                className="max-h-64 w-full rounded-xl border border-gray-200 object-contain"
              />
              <button
                type="button"
                onClick={() => {
                  atualizar('arquiteturaDataUrl', '');
                }}
                className="mt-2 text-xs font-semibold text-red-600 hover:underline"
              >
                Remover diagrama
              </button>
            </div>
          )}
        </Secao>

        <Secao titulo="Plano de suporte">
          <Selecao
            id="plano"
            rotulo="Plano SafePlace"
            valor={valores.planoSuporte}
            opcoes={PLANOS_POR_TRILHA[valores.trilha].map((p) => ({
              valor: p,
              rotulo: NOME_COMERCIAL_PLANO[p],
            }))}
            onChange={(p) => {
              atualizar('planoSuporte', p);
            }}
          />

          {valores.trilha === 'CS' && (
            <Selecao
              id="duracao"
              rotulo="Duração do contrato"
              valor={valores.duracaoContratoMeses}
              opcoes={[
                { valor: 12, rotulo: '12 meses' },
                { valor: 36, rotulo: '36 meses — margem 2 p.p. menor' },
              ]}
              onChange={(d) => {
                atualizar('duracaoContratoMeses', d);
              }}
            />
          )}

          {comCsv && (
            <Aviso tom="atencao">
              Trocar o plano ou a duração <strong>altera o preço da infraestrutura</strong>, não só
              o custo do suporte: a margem aplicada depende dessa combinação.
            </Aviso>
          )}

          {valores.planoSuporte === 'nenhum' && (
            <Aviso tom="informacao">
              Sem plano gerenciado, o suporte não é cobrado e a margem usa a faixa Developer. A
              proposta explica que a operação segue com a equipe do cliente.
            </Aviso>
          )}
        </Secao>

        <Secao titulo="Crédito AWS e implantação">
          <Marcador
            id="credito"
            rotulo="Pleitear Crédito Especial AWS via eMaster"
            marcado={valores.incluiCredito}
            onChange={(v) => {
              setValores((atual) => {
                /*
                 * Pleitear crédito já assume a implantação coberta por ele — é o arranjo usado na
                 * quase totalidade das propostas, e deixá-lo como padrão poupa um passo.
                 * Só se aplica enquanto o vendedor não escolheu a modalidade à mão.
                 */
                if (v) {
                  return {
                    ...atual,
                    incluiCredito: true,
                    modalidadeDeploy: modalidadeDeployEditada
                      ? atual.modalidadeDeploy
                      : 'financiado',
                  };
                }

                /*
                 * Sem crédito, "Coberta pelo crédito AWS" viraria afirmação falsa no documento do
                 * cliente: o motor não tem crédito para abater, então a implantação seria cobrada
                 * enquanto a proposta anunciaria que está coberta. Cai para faturada, que preserva a
                 * intenção de incluir a implantação sem mentir sobre quem paga.
                 */
                return {
                  ...atual,
                  incluiCredito: false,
                  modalidadeDeploy:
                    atual.modalidadeDeploy === 'financiado' ? 'faturado' : atual.modalidadeDeploy,
                };
              });
            }}
            dica="A proposta deixa explícito que a concessão é decisão da AWS. Já assume a implantação coberta pelo crédito."
          />

          {valores.incluiCredito && (
            <>
              <Numero
                id="credito-valor"
                rotulo="Valor do crédito (US$)"
                prefixo="US$"
                valor={valores.creditoValorUsd}
                onChange={(v) => {
                  atualizar('creditoValorUsd', v ?? 0);
                }}
              />
              <Selecao
                id="credito-janela"
                rotulo="Distribuição"
                valor={valores.creditoJanelaMeses}
                opcoes={[3, 4, 5, 6, 12].map((m) => ({
                  valor: m as 3 | 4 | 5 | 6 | 12,
                  rotulo: `Primeiros ${m} meses`,
                }))}
                onChange={(m) => {
                  atualizar('creditoJanelaMeses', m);
                }}
              />
            </>
          )}

          <Selecao
            id="deploy"
            rotulo="Implantação"
            valor={valores.modalidadeDeploy}
            opcoes={[
              { valor: 'nao_incluido', rotulo: 'Não incluir' },
              { valor: 'faturado', rotulo: 'Faturada, pagamento único' },
              { valor: 'financiado', rotulo: 'Coberta pelo crédito AWS' },
              { valor: 'cortesia', rotulo: 'Cortesia' },
            ]}
            onChange={(m) => {
              setModalidadeDeployEditada(true);
              atualizar('modalidadeDeploy', m);
            }}
            {...(valores.incluiCredito &&
              valores.modalidadeDeploy === 'financiado' && {
                dica: 'O crédito pleiteado abate a implantação antes de reduzir a mensalidade.',
              })}
          />

          {valores.modalidadeDeploy !== 'nao_incluido' && (
            <Numero
              id="deploy-custo"
              rotulo={`Custo da implantação (${moedaEntrada})`}
              prefixo={moedaEntrada}
              valor={valores.deployCusto}
              onChange={(v) => {
                setDeployEditado(true);
                atualizar('deployCusto', v ?? 0);
              }}
              dica={
                deployEditado
                  ? 'Valor definido por você.'
                  : 'Sugerido em um mês de consumo. Editar congela o valor.'
              }
            />
          )}
        </Secao>

        <Secao
          titulo="Produtos e benefícios eMaster"
          descricao="Produtos e cortesias que a eMaster acrescenta à proposta. Cada um gera a própria apresentação no documento."
        >
          <Marcador
            id="backup"
            rotulo="eMaster Backup On-Premises"
            marcado={valores.incluiBackup}
            onChange={(v) => {
              atualizar('incluiBackup', v);
            }}
          />

          {valores.incluiBackup && (
            <div className="grid grid-cols-2 gap-3">
              <Numero
                id="bk-file"
                rotulo="File Server"
                passo="1"
                valor={valores.backupFileServer}
                onChange={(v) => {
                  atualizar('backupFileServer', Math.trunc(v ?? 0));
                }}
              />
              <Numero
                id="bk-virtual"
                rotulo="Virtual Server"
                passo="1"
                valor={valores.backupVirtualServer}
                onChange={(v) => {
                  atualizar('backupVirtualServer', Math.trunc(v ?? 0));
                }}
              />
              <Numero
                id="bk-sp"
                rotulo="Sharepoint por usuário"
                passo="1"
                valor={valores.backupSharepoint}
                onChange={(v) => {
                  atualizar('backupSharepoint', Math.trunc(v ?? 0));
                }}
              />
              <Numero
                id="bk-vmware"
                rotulo="VMware ou Hyper-V"
                passo="1"
                valor={valores.backupVmwareHyperv}
                onChange={(v) => {
                  atualizar('backupVmwareHyperv', Math.trunc(v ?? 0));
                }}
              />
            </div>
          )}

          <Marcador
            id="safestorage"
            rotulo="eMaster SafeStorage"
            marcado={valores.incluiSafeStorage}
            onChange={(v) => {
              atualizar('incluiSafeStorage', v);
            }}
            dica="Plataforma SaaS de arquivos corporativos sobre a AWS. A assinatura é por faixa de usuários; o armazenamento S3 entra no consumo da proposta."
          />

          {valores.incluiSafeStorage && (
            <div className="space-y-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
              <Numero
                id="ss-usuarios"
                rotulo="Usuários"
                passo="1"
                obrigatorio
                valor={valores.safestorageUsuarios}
                onChange={(v) => {
                  atualizar('safestorageUsuarios', Math.trunc(v ?? 0));
                }}
                dica="Define a faixa da assinatura. A sugestão abaixo acompanha este número até você escolher a faixa manualmente."
              />

              <Selecao
                id="ss-plano"
                rotulo="Faixa da assinatura"
                valor={valores.safestoragePlano}
                opcoes={CONFIGURACAO_PADRAO.safestorage.faixas.map((f) => ({
                  valor: f.plano,
                  rotulo:
                    f.mensalidadeUsd === null
                      ? `${f.nomeComercial} — acima de 50 usuários, sob consulta`
                      : `${f.nomeComercial} — até ${String(f.usuariosAte)} usuários, ${formatarUsd(f.mensalidadeUsd)}/mês`,
                }))}
                onChange={(p) => {
                  setFaixaSafeStorageEditada(true);
                  atualizar('safestoragePlano', p);
                }}
              />

              {valores.safestoragePlano === 'enterprise' && (
                <Numero
                  id="ss-mensalidade"
                  rotulo="Mensalidade negociada (US$)"
                  prefixo="US$"
                  obrigatorio
                  valor={valores.safestorageMensalidadeUsd}
                  onChange={(v) => {
                    atualizar('safestorageMensalidadeUsd', v);
                  }}
                  dica="A faixa Enterprise é sob consulta: sem este valor a publicação fica bloqueada."
                />
              )}

              <Aviso tom="informacao">
                O <strong>armazenamento não está nesta assinatura</strong>. Lance o S3 pelo CSV da
                calculadora ou no campo de consumo mensal — ele recebe a margem da faixa Developer,
                e a proposta explica ao cliente que o espaço é faturado sob demanda.
              </Aviso>

              {/*
                O aviso só aparece quando a faixa muda de fato. Em PS a margem é fixa, e em CS os
                planos "nenhum" e Developer já usam a faixa do Developer — avisar nesses casos seria
                alarme sobre uma conta que não mudou.
              */}
              {valores.trilha === 'CS' &&
              (valores.planoSuporte === 'business' || valores.planoSuporte === 'enterprise') ? (
                <Aviso tom="atencao">
                  Com o SafeStorage na proposta, o armazenamento é vendido com a{' '}
                  <strong>margem da faixa Developer</strong>, e não com a do{' '}
                  {NOME_COMERCIAL_PLANO[valores.planoSuporte]}. A prévia registra a troca nos avisos
                  do cálculo.
                </Aviso>
              ) : null}
            </div>
          )}

          <Marcador
            id="custos-adicionais"
            rotulo="Custos Adicionais"
            marcado={valores.incluiCustosAdicionais}
            onChange={(v) => {
              atualizar('incluiCustosAdicionais', v);
            }}
            dica={`Serviços cobrados à parte, com nome e valor definidos por você. Ex.: Módulo SafePlace 24x7. O valor é mensal, em ${moedaEntrada}, e entra na mensalidade sem margem — é o preço final.`}
          />

          {valores.incluiCustosAdicionais && (
            <div className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
              {valores.custosAdicionais.map((item, indice) => (
                /*
                 * A chave é o índice porque a linha não tem identidade própria: o vendedor pode digitar
                 * duas com o mesmo nome, e o nome muda enquanto ele escreve. Índice é estável aqui
                 * porque a remoção reconstrói a lista inteira no mesmo render.
                 */
                <div key={indice} className="flex items-end gap-2">
                  <div className="flex-1">
                    <Texto
                      id={`custo-adicional-descricao-${String(indice)}`}
                      rotulo="Produto ou serviço"
                      rotuloOculto={indice > 0}
                      valor={item.descricao}
                      placeholder="Módulo SafePlace 24x7"
                      onChange={(descricao) => {
                        atualizar(
                          'custosAdicionais',
                          valores.custosAdicionais.map((i, n) =>
                            n === indice ? { ...i, descricao } : i,
                          ),
                        );
                      }}
                    />
                  </div>
                  <div className="w-40">
                    <Numero
                      id={`custo-adicional-valor-${String(indice)}`}
                      rotulo={`Valor mensal (${moedaEntrada})`}
                      rotuloOculto={indice > 0}
                      prefixo={moedaEntrada}
                      valor={item.valorMensal}
                      onChange={(valorMensal) => {
                        atualizar(
                          'custosAdicionais',
                          valores.custosAdicionais.map((i, n) =>
                            n === indice ? { ...i, valorMensal: valorMensal ?? 0 } : i,
                          ),
                        );
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    aria-label={`Remover ${item.descricao === '' ? `linha ${String(indice + 1)}` : item.descricao}`}
                    disabled={valores.custosAdicionais.length === 1}
                    onClick={() => {
                      atualizar(
                        'custosAdicionais',
                        valores.custosAdicionais.filter((_, n) => n !== indice),
                      );
                    }}
                    className="mb-1 rounded-lg px-3 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                  >
                    Remover
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  atualizar('custosAdicionais', [
                    ...valores.custosAdicionais,
                    { descricao: '', valorMensal: 0 },
                  ]);
                }}
                className="text-xs font-semibold text-brand-accent hover:underline"
              >
                + Adicionar outro custo
              </button>

              <Aviso tom="informacao">
                Linha sem nome é ignorada. O valor entra na mensalidade <strong>sem margem</strong>:
                informe o preço final ao cliente.
                {valores.trilha === 'PS'
                  ? ' Na trilha PS o valor é em reais e é convertido pela cotação declarada na proposta.'
                  : ''}
              </Aviso>
            </div>
          )}

          <Marcador
            id="assessment"
            rotulo="Incluir Assessment SafePlace de cortesia"
            marcado={valores.incluiAssessment}
            onChange={(v) => {
              atualizar('incluiAssessment', v);
            }}
            dica="Avaliação do ambiente pelo AWS Well-Architected Framework, entregue sem custo. Gera uma seção própria na proposta, com escopo e entregáveis declarados, e consta no termo formal."
          />

          {valores.incluiAssessment && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <Selecao
                id="assessment-foco"
                rotulo="Qual avaliação"
                valor={valores.assessmentEscolha}
                opcoes={[
                  {
                    valor: 'custos',
                    rotulo: `${FOCO_ASSESSMENT_CURTO.custos} (Cost Optimization)`,
                  },
                  {
                    valor: 'seguranca',
                    rotulo: `${FOCO_ASSESSMENT_CURTO.seguranca} (Security)`,
                  },
                  { valor: 'ambos', rotulo: 'Ambas as avaliações' },
                ]}
                onChange={(e) => {
                  atualizar('assessmentEscolha', e);
                }}
                dica="Cada avaliação vira um bloco próprio na proposta, com o pilar, o escopo analisado e os entregáveis."
              />
            </div>
          )}

          <Marcador
            id="treinamento"
            rotulo="Incluir sessões de treinamento AWS Showroom"
            marcado={valores.incluiTreinamento}
            onChange={(v) => {
              atualizar('incluiTreinamento', v);
            }}
            dica="Capacitação do time do cliente conduzida por arquitetos certificados AWS, entregue sem custo. Gera uma seção própria na proposta e consta no termo formal."
          />

          {valores.incluiTreinamento && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <Selecao
                id="treinamento-sessoes"
                rotulo="Quantas sessões"
                valor={valores.sessoesTreinamento}
                opcoes={[1, 2, 3].map((n) => ({
                  valor: n,
                  rotulo: `${String(n)} ${n === 1 ? 'sessão' : 'sessões'}`,
                }))}
                onChange={(n) => {
                  atualizar('sessoesTreinamento', n);
                }}
                dica="A quantidade é o compromisso registrado no termo formal. A pauta de cada sessão é acordada com o cliente depois."
              />
            </div>
          )}
        </Secao>
      </div>

      <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
        {previa.problemas.length > 0 && (
          <Aviso tom="atencao">
            <p className="mb-2 font-semibold">Para gerar a prévia, falta:</p>
            <ul className="list-inside list-disc space-y-1">
              {previa.problemas.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Aviso>
        )}

        <Resumo previa={previa} />

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-2 border-b border-gray-100 px-3 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-4">
            <h2 className="text-sm font-bold text-brand-blue">Prévia do documento</h2>
            <div className="flex flex-wrap items-center gap-2">
              {previa.html !== null && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      const iframe = document.querySelector<HTMLIFrameElement>(
                        'iframe[title="Prévia da proposta"]',
                      );
                      iframe?.contentWindow?.print();
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-200"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.8}
                      stroke="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z"
                      />
                    </svg>
                    <span className="hidden sm:inline">Imprimir / PDF</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (previa.html === null) return;
                      const blob = new Blob([previa.html], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `proposta-${
                        valores.cliente
                          .trim()
                          .toLowerCase()
                          .replace(/[^a-z0-9]/g, '-') || 'rascunho'
                      }.html`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-200"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.8}
                      stroke="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                      />
                    </svg>
                    <span className="hidden sm:inline">Baixar HTML</span>
                  </button>
                  {modoEdicao && (
                    <input
                      type="text"
                      value={motivoAlteracao}
                      onChange={(e) => {
                        setMotivoAlteracao(e.target.value);
                      }}
                      placeholder="Motivo da alteração (obrigatório)"
                      aria-label="Motivo da alteração"
                      className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    />
                  )}
                  <button
                    type="button"
                    disabled={
                      publicando ||
                      previa.documento === null ||
                      perfilPessoal === null ||
                      !perfilPessoal.cadastroCompleto ||
                      (modoEdicao && motivoAlteracao.trim() === '')
                    }
                    title={
                      perfilPessoal !== null && !perfilPessoal.cadastroCompleto
                        ? 'Complete seu cadastro (telefone e cargo) em Meu Perfil antes de publicar.'
                        : undefined
                    }
                    onClick={() => {
                      if (previa.documento === null) return;
                      void handlePublicar();
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-accent px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.8}
                      stroke="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z"
                      />
                    </svg>
                    {publicando
                      ? modoEdicao
                        ? 'Atualizando...'
                        : 'Publicando...'
                      : modoEdicao
                        ? 'Republicar'
                        : 'Publicar'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/*
            Link persistente da proposta publicada. O `alert` da publicação desaparece ao ser
            fechado, e com ele a URL — que é justamente o que o vendedor precisa copiar para enviar
            ao cliente.
          */}
          {urlPublicada !== null && (
            <div className="flex flex-col gap-2 border-b border-green-200 bg-green-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-green-800">
                  Proposta publicada
                </p>
                <a
                  href={urlPublicada}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-sm text-green-900 underline hover:no-underline"
                >
                  {urlPublicada}
                </a>
                {linkRotacionado && (
                  <p className="mt-1 text-xs font-semibold text-amber-800">
                    O endereço mudou por segurança. O link enviado antes ao cliente não abre mais —
                    reenvie este.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(urlPublicada);
                }}
                className="shrink-0 rounded-lg bg-green-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-green-800"
              >
                Copiar link
              </button>
            </div>
          )}

          {previa.html === null ? (
            <p className="px-4 py-16 text-center text-sm text-gray-500">
              A prévia aparece quando os campos obrigatórios estiverem preenchidos.
            </p>
          ) : (
            <iframe
              title="Prévia da proposta"
              srcDoc={previa.html}
              sandbox="allow-same-origin allow-scripts"
              className="h-[50vh] w-full border-0 sm:h-[60vh] lg:h-[80vh]"
            />
          )}
        </div>
      </div>
    </div>
  );
}
